export const STOP_ACTION = { DELIVERY: "delivery", PICKUP: "pickup" } as const
export type StopAction = (typeof STOP_ACTION)[keyof typeof STOP_ACTION]
export const STOP_ACTION_LABEL: Record<StopAction, string> = { [STOP_ACTION.DELIVERY]: "Entrega", [STOP_ACTION.PICKUP]: "Recojo" }

export const STOP_STATUS = { PENDING: "pending", CONFIRMED: "confirmed", IN_TRANSIT: "in_transit", COMPLETED: "completed", CANCELLED: "cancelled" } as const
export type StopStatus = (typeof STOP_STATUS)[keyof typeof STOP_STATUS]
export const STOP_STATUS_LABEL: Record<StopStatus, string> = {
  [STOP_STATUS.PENDING]: "Pendiente",
  [STOP_STATUS.CONFIRMED]: "Confirmado",
  [STOP_STATUS.IN_TRANSIT]: "En Ruta",
  [STOP_STATUS.COMPLETED]: "Finalizado",
  [STOP_STATUS.CANCELLED]: "Cancelado"
}

export const ODOO_STATE_LABEL: Record<string, string> = {
  sin_gestion: "Sin gestión",
  en_gestion: "En gestión",
  material_validado: "Material validado",
  preparado: "Preparado",
  preparado_parcial: "Preparado parcialmente",
  retiro_de_oficina: "Retiro de oficina",
  llegada_a_establecimiento: "Llegada a establecimiento",
  entregado: "Entregado",
  llegada_de_instrumentista: "Llegada de instrumentista",
  en_cirugia: "Inicio de cirugía",
  cirugia_finalizada: "Fin de cirugía",
  listo_para_recojo: "Listo para recojo",
  recojo_programado: "Recojo programado",
  llegada_recojo: "Llegada recojo",
  recogido: "Recogido",
  retorno_a_oficina: "Retorno a oficina",
  entrega_en_oficina: "Entrega en oficina",
  enviado_a_facturar: "Enviado a facturar",
  procesado: "Procesado",
  suspendida_c_d: "Suspendida c/d",
  suspendida_s_d: "Suspendida s/d",
  no_aplica: "No aplica"
}
export const ODOO_STATE_COLOR: Record<string, string> = {
  sin_gestion: "gray",
  en_gestion: "blue",
  material_validado: "cyan",
  preparado: "indigo",
  preparado_parcial: "yellow",
  retiro_de_oficina: "violet",
  llegada_a_establecimiento: "teal",
  entregado: "green",
  llegada_de_instrumentista: "lime",
  en_cirugia: "yellow",
  cirugia_finalizada: "orange",
  listo_para_recojo: "pink",
  recojo_programado: "grape",
  llegada_recojo: "grape",
  recogido: "green",
  retorno_a_oficina: "grape",
  entrega_en_oficina: "teal",
  enviado_a_facturar: "blue",
  procesado: "green",
  suspendida_c_d: "red",
  suspendida_s_d: "red",
  no_aplica: "gray"
}

export const PRODUCTO_TIPO = { ACTIVO: "activo", BANDEJA: "bandeja", SUELTO: "suelto", PAQUETE: "paquete" } as const
export type ProductoTipo = (typeof PRODUCTO_TIPO)[keyof typeof PRODUCTO_TIPO]

export interface DeliveryStopLot {
  lotId?: number
  lotName?: string
  qty?: number
}

/** Activo fijo propio de una bandeja (viene dentro del payload de la bandeja) */
export interface DeliveryStopBandejaActivo {
  id?: number
  name?: string
  code?: string
}

/** Producto/activo/bandeja/paquete dentro de un ítem de paciente de un punto de entrega */
export interface DeliveryStopProducto {
  tipo?: ProductoTipo
  // Sueltos (líneas de picking)
  productId?: number
  productCode?: string
  productName?: string
  moveId?: number
  pickingId?: number
  pickingName?: string
  /** Paquete: sus ítems traen el albarán de cada uno (sirve para saber el centro del punto) */
  paqueteItems?: { pickingName?: string }[]
  lots?: DeliveryStopLot[]
  // Bandejas
  trayId?: string
  trayName?: string
  codigoUnico?: string
  odooBandejaId?: number
  activos?: DeliveryStopBandejaActivo[]
  // Activos sueltos (identidad propia, aparte del producto genérico vinculado)
  activoId?: number
  activoName?: string
  activoCode?: string
  // Paquetes
  paqueteId?: number
  paqueteName?: string
}

/** Ítem de un punto de entrega agrupado por paciente/SO */
export interface DeliveryStopItem {
  patientName?: string
  soId?: number
  soName?: string
  doctorName?: string
  surgeryDate?: string
  isEmergency?: boolean
  productos?: DeliveryStopProducto[]
}

export interface AvailablePickingItem {
  pickingId: number
  pickingName: string
  moveId: number
  productId: number
  productCode: string
  productName: string
  qtyOrdered: number
  qtyAvailable: number
}

export interface AvailableTrayItem {
  trayId: string
  trayName: string
  codigoUnico: string
  isAvailable: boolean
  guideId?: string
  nGuide?: string
}

export interface AvailableCirugia {
  soId: number
  soName: string
  patientName?: string
  doctorName?: string
  clinicName?: string
  surgeryDate?: string
  odooState?: string
  pickingItems: AvailablePickingItem[]
  trayItems: AvailableTrayItem[]
}

export interface AvailableItemsResponse {
  cirugias: AvailableCirugia[]
}

export interface RiderRef {
  id: string
  name: string
  type: string
  phone?: string
  vehiclePlate?: string
  supabaseUserId?: string
  isActive?: boolean
  /** País del repartidor (sin valor = Perú) */
  country?: string
}

export interface DeliveryStop {
  id: string
  /** Solo en recojos de Odoo: la oficina (destino final). El origen es la clínica (`address`). */
  officeAddress?: string
  rider?: RiderRef | string
  scheduledDate?: string
  action?: StopAction
  address?: string
  addressLat?: number
  addressLng?: number
  status?: StopStatus
  completedAt?: string
  cancelledAt?: string
  suggestedOrder?: number
  legDistanceMeters?: number
  legDurationSeconds?: number
  notes?: string
  odooState?: string
  /** Perú: PE-AAAA-MM-NNN (entrega) / PR-AAAA-MM-NNN (recojo); Ecuador: EC-PE-… / EC-PR-… */
  correlativo?: string
  /** País del punto: "Perú" | "Ecuador". Sin valor = Perú (todo lo anterior a Ecuador) */
  country?: string
  /** Centro elegido a mano para el punto (LIM, TRU, UIO...); manda sobre el que sale de los albaranes. Sin valor = el de los albaranes */
  center?: string | null
  ventanaEntrega?: { zonaReparto?: string; tiempoLlegadaMin?: number; horaLimiteRecepcionImplante?: string }
  ventanaRecojo?: { diasHabilitados?: string[]; horaRecojoInstrumental?: string; horaDesde?: string; horaHasta?: string }
  items?: DeliveryStopItem[]
  /** Cuántos productos se agregaron automáticamente después de creado (solo mientras status=pending) — aviso en la tabla */
  newProductsCount?: number
  createdAt?: string
  updatedAt?: string
}

/** Un despachador en el tablero de programación diaria, con su carga del día (entregas y recojos mezclados) */
export interface DailyScheduleRider {
  rider: { id: string; name: string; type: string; phone?: string; supabaseUserId?: string; country?: string }
  pendingCount: number
  completedCount: number
  isAvailable: boolean
  deliveryCount: number
  pickupCount: number
  /** Recojos asignados que aún no se confirman (listo_para_recojo): no están en su programación hasta confirmarlos */
  toConfirmCount: number
  stops: DeliveryStop[]
}

export interface DailySchedule {
  date: string
  riders: DailyScheduleRider[]
  unassigned: DeliveryStop[]
}

export interface AssignStopsResult {
  assigned: number
  skipped: { id: string; reason: string }[]
}

/** Un recojo con despachador asignado pero sin confirmar: se ve en el tablero, pero todavía no es "recojo programado" */
export const isPickupToConfirm = (stop: Pick<DeliveryStop, "action" | "odooState">): boolean =>
  stop.action === STOP_ACTION.PICKUP && stop.odooState === "listo_para_recojo"

export const PENDING_GROUP_STATUS = { PENDING: "pending", INCLUDED: "included", DEFERRED: "deferred" } as const
export type PendingGroupStatus = (typeof PENDING_GROUP_STATUS)[keyof typeof PENDING_GROUP_STATUS]

/** Grupo de productos nuevos de una SO cuyo punto de entrega ya está confirmado/completado — a la espera de que César decida incluirlos */
export interface PendingProductGroup {
  id: string
  soId?: number
  soName?: string
  action?: StopAction
  patientName?: string
  doctorName?: string
  surgeryDate?: string
  isEmergency?: boolean
  originStopId?: string
  originStopCorrelativo?: string
  tipo?: ProductoTipo
  productos?: DeliveryStopProducto[]
  motivo?: string
  status?: PendingGroupStatus
  createdAt?: string
}

export interface SaveDeliveryStopDto {
  rider?: string
  /** Centro del punto; null = quitarlo (vuelve al de los albaranes) */
  center?: string | null
  scheduledDate?: string
  action?: StopAction
  address?: string
  notes?: string
  items?: DeliveryStopItem[]
}

export interface QueryDeliveryStopParams {
  page?: number
  rows?: number
  sortColumn?: string
  sortDirection?: "asc" | "desc"
  riderId?: string
  scheduledDate?: string
  scheduledDateFrom?: string
  scheduledDateTo?: string
  action?: string
  status?: string
  // Excluye 'completed' cuando no hay un status explícito elegido -- pedido
  // explícito del usuario 2026-09-16: un punto completado ya se hizo y debe
  // salir de "Puntos de entrega" por defecto (sigue accesible filtrando por
  // "Completado" a propósito, ver columna Estado en DeliveryStopsPage).
  excludeCompleted?: boolean
  search?: string
  /** "Perú" (incluye los puntos sin país) | "Ecuador". Solo lo respeta para el admin; al resto el back le recorta su país */
  country?: string
}

export interface DeliveryStopOdooSyncResult {
  agregadas?: number
  yaExistian?: number
  total?: number
  fechaDesde?: string
  fechaHasta?: string
}

export interface OptimizeRouteRequest {
  riderId: string
  scheduledDate: string
  originAddress?: string
}

export interface RiderRoute {
  id: string
  rider?: string
  scheduledDate?: string
  originAddress?: string
  originLat?: number
  originLng?: number
  stopsOrder?: string[]
  unresolvedStopIds?: string[]
  totalDistanceMeters?: number
  totalDurationSeconds?: number
  encodedPolyline?: string
  optimizedAt?: string
}
